import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  ArrowLeftIcon,
  PlusIcon, 
  TrashIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';

const ReturnForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const returnType = queryParams.get('type') || 'customer';
  
  const { 
    addReturn, 
    getProducts, 
    getCustomers,
    getSuppliers,
    getPurchases,
    fetchAllData 
  } = useAppData();
  
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);
  const [entitySearchQuery, setEntitySearchQuery] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [entities, setEntities] = useState([]);
  
  // Helper to consistently render an entity name
  const getEntityName = (ent) => {
    if (!ent) return '';
    const full = `${ent.firstName || ''} ${ent.lastName || ''}`.trim();
    if (full) return full;
    if (ent.name && ent.name.trim() !== '') return ent.name;
    if (ent.company && ent.company.trim() !== '') return ent.company;
    return String(ent.id || '');
  };
  
  const [returnData, setReturnData] = useState({
    returnNumber: '',
    date: new Date().toISOString().split('T')[0],
    entityId: '',
    entityName: '',
    entityEmail: '',
    entityPhone: '',
    entityCompany: '',
    entityAddress: '',
    entityWilaya: '',
    reason: 'defective',
    status: 'pending',
    refundMethod: returnType === 'customer' ? 'credit' : 'credit',
    notes: '',
    items: [],
    entityHistory: []
  });
  
  useEffect(() => {
    // Générer un numéro de retour automatique
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const randomDigits = Math.floor(Math.random() * 999).toString().padStart(3, '0');
    const prefix = returnType === 'customer' ? 'RET-C' : 'RET-F';
    
    setReturnData(prev => ({
      ...prev,
      returnNumber: `${prefix}-${year}-${randomDigits}`
    }));
  }, [returnType]);
  
  // Charger les données des produits et entités (clients ou fournisseurs)
  const loadEntities = () => {
    // Récupérer les produits
    const loadedProducts = getProducts();
    setProducts(loadedProducts.map(product => ({
      id: product.id,
      code: product.sku || product.id,
      name: product.name,
      price: parseFloat(product.price) || 0
    })));
    
    // Récupérer les clients ou fournisseurs selon le type de retour avec données enrichies
    if (returnType === 'customer') {
      // Utiliser getCustomers() pour obtenir les données les plus récentes
      const customers = getCustomers() || [];
      setEntities(customers.map((customer) => ({ 
        id: customer.id, 
        name: getEntityName(customer),
        email: customer.email,
        phone: customer.phone,
        totalTransactions: customer.totalTransactions || 0,
        totalSpent: customer.totalSpent || 0,
        lastTransaction: customer.lastTransaction || '',
        fullData: customer // Store full customer data for reference
      })));
    } else {
      // Utiliser getSuppliers() pour obtenir les données les plus récentes
      const suppliers = getSuppliers() || [];
      setEntities(suppliers.map((supplier) => ({ 
        id: supplier.id, 
        name: getEntityName(supplier),
        email: supplier.email,
        phone: supplier.phone,
        company: supplier.company,
        address: supplier.address,
        wilaya: supplier.wilaya,
        totalOrders: supplier.totalOrders || 0,
        lastOrder: supplier.lastOrder || '',
        totalPurchases: supplier.totalPurchases || 0,
        fullData: supplier // Store full supplier data for reference
      })));
    }
  };
  
  // Charger les données au montage du composant
  useEffect(() => {
    // Rafraîchir les données depuis le stockage local
    fetchAllData().then(() => {
      loadEntities();
    });
  }, [returnType]);
  
  // Rafraîchir les données lorsque le composant devient visible
  useEffect(() => {
    const handleFocus = () => {
      console.log('Window focus detected, refreshing data...');
      fetchAllData().then(() => {
        loadEntities();
        
        // Reload entity history if an entity is selected
        if (returnData.entityId) {
          loadEntityHistory(returnData.entityId);
        }
      });
    };
    
    // Rafraîchir les données périodiquement (toutes les 15 secondes)
    // Intervalle plus court pour garantir des données très récentes
    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        console.log('Periodic refresh, updating data...');
        fetchAllData().then(() => {
          loadEntities();
          
          // Reload entity history if an entity is selected
          if (returnData.entityId) {
            loadEntityHistory(returnData.entityId);
          }
        });
      }
    }, 15000); // 15 secondes pour une mise à jour plus fréquente
    
    // Rafraîchir les données lorsque la visibilité de la page change
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('Page visibility changed to visible, refreshing data...');
        fetchAllData().then(() => {
          loadEntities();
          
          // Reload entity history if an entity is selected
          if (returnData.entityId) {
            loadEntityHistory(returnData.entityId);
          }
        });
      }
    };
    
    // Rafraîchir les données lorsque le stockage local change
    // Cela permettra de détecter les changements effectués dans d'autres onglets/fenêtres
    const handleStorageChange = (event) => {
      if (event.key === 'stockpro_purchases' || event.key === null) {
        console.log('Local storage changed, refreshing purchase data...');
        fetchAllData().then(() => {
          loadEntities();
          
          // Reload entity history if a supplier is selected
          if (returnData.entityId && returnType === 'supplier') {
            loadEntityHistory(returnData.entityId);
          }
        });
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(refreshInterval);
    };
  }, [returnData.entityId, returnType]);
  
  const reasonOptions = returnType === 'customer' 
    ? [
        { value: 'defective', label: 'Défectueux' },
        { value: 'wrong_size', label: 'Mauvaise taille' },
        { value: 'wrong_item', label: 'Mauvais produit' },
        { value: 'damaged', label: 'Endommagé' },
        { value: 'customer_choice', label: 'Choix du client' }
      ]
    : [
        { value: 'defective', label: 'Défectueux' },
        { value: 'wrong_item', label: 'Erreur de livraison' },
        { value: 'damaged', label: 'Endommagé pendant le transport' },
        { value: 'expired', label: 'Produit expiré/périmé' },
        { value: 'quality', label: 'Qualité non conforme' }
      ];

  const refundMethodOptions = returnType === 'customer' 
    ? [
        { value: 'credit', label: 'Avoir client' },
        { value: 'exchange', label: 'Échange produit' },
        { value: 'cash', label: 'Remboursement espèces' },
        { value: 'bank', label: 'Remboursement bancaire' }
      ]
    : [
        { value: 'credit', label: 'Avoir fournisseur' },
        { value: 'exchange', label: 'Remplacement produit' },
        { value: 'refund', label: 'Remboursement' },
        { value: 'deduction', label: 'Déduction prochaine facture' }
      ];

  // Gérer la recherche de produits
  const filteredProducts = products.filter(product => 
    product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.code.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleAddProduct = (product) => {
    setSelectedProducts([...selectedProducts, {
      ...product,
      quantity: 1,
      total: product.price
    }]);
    setShowProductSearch(false);
    setSearchQuery('');
  };
  
  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };
  
  const handleQuantityChange = (productId, quantity) => {
    const newQuantity = Math.max(1, parseInt(quantity) || 1);
    setSelectedProducts(selectedProducts.map(p => 
      p.id === productId 
        ? { ...p, quantity: newQuantity, total: p.price * newQuantity } 
        : p
    ));
  };
  
  const calculateTotal = () => {
    return selectedProducts.reduce((sum, product) => sum + product.total, 0);
  };
  
  // Charger l'historique des transactions du client ou fournisseur
  const loadEntityHistory = async (entityId) => {
    if (!entityId) return;
    
    try {
      if (returnType === 'customer') {
        // Charger l'historique des ventes pour un client
        const allSales = localStorage.getItem('stockpro_sales');
        if (allSales) {
          const parsedSales = JSON.parse(allSales);
          const customerSales = parsedSales
            .filter(sale => sale.customerId === entityId)
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5); // Prendre les 5 dernières transactions
          
          setReturnData(prev => ({
            ...prev,
            entityHistory: customerSales
          }));
          
          console.log(`Historique client chargé: ${customerSales.length} transactions récentes`);
        }
      } else {
        // Charger l'historique des achats pour un fournisseur en temps réel
        // Utiliser les données du contexte si disponibles, sinon charger depuis localStorage
        let supplierPurchases = [];
        
        // Récupérer les achats depuis localStorage pour garantir les données les plus récentes
        const allPurchases = localStorage.getItem('stockpro_purchases');
        if (allPurchases) {
          const parsedPurchases = JSON.parse(allPurchases);
          supplierPurchases = parsedPurchases
            .filter(purchase => purchase.supplierId === entityId)
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .slice(0, 5); // Prendre les 5 derniers achats
          
          setReturnData(prev => ({
            ...prev,
            entityHistory: supplierPurchases
          }));
          
          console.log(`Historique fournisseur chargé: ${supplierPurchases.length} achats récents`);
        } else {
          console.log('Aucun historique d\'achat trouvé pour ce fournisseur');
        }
      }
    } catch (error) {
      console.error(`Erreur lors du chargement de l'historique ${returnType === 'customer' ? 'client' : 'fournisseur'}:`, error);
      toast.error(`Erreur lors du chargement de l'historique ${returnType === 'customer' ? 'client' : 'fournisseur'}`);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (returnData.entityId === '') {
      toast.error(`Veuillez sélectionner un ${returnType === 'customer' ? 'client' : 'fournisseur'}`);
      return;
    }
    
    if (selectedProducts.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Préparer les données du retour
      const selectedEntity = entities.find(entity => entity.id === returnData.entityId);
      const returnToSave = {
        ...returnData,
        type: returnType,
        items: selectedProducts,
        totalAmount: calculateTotal(),
        createdAt: new Date().toISOString(),
        entityDetails: selectedEntity?.fullData || null // Stocker les données complètes du client/fournisseur
      };
      
      // Ajouter le retour à la base de données locale
      const savedReturn = await addReturn(returnToSave);
      console.log('Retour enregistré avec succès:', savedReturn);
      
      // Forcer la persistance dans localStorage
      const currentReturns = localStorage.getItem('stockpro_returns');
      let returnsArray = [];
      
      if (currentReturns) {
        try {
          returnsArray = JSON.parse(currentReturns);
        } catch (e) {
          console.error('Erreur lors de la lecture des retours existants:', e);
          returnsArray = [];
        }
      }
      
      // Ajouter le nouveau retour s'il n'existe pas déjà
      if (!returnsArray.some(r => r.id === savedReturn.id)) {
        returnsArray.push(savedReturn);
        localStorage.setItem('stockpro_returns', JSON.stringify(returnsArray));
        console.log('Retour sauvegardé dans localStorage:', returnsArray.length, 'retours au total');
      }
      
      // Rafraîchir les données
      await fetchAllData();
      
      toast.success('Retour enregistré avec succès');
      navigate('/returns');
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du retour:', error);
      toast.error('Erreur lors de l\'enregistrement du retour');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/returns')}
            className="p-2 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 text-white hover:bg-white/20 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">
              {returnType === 'customer' ? 'Nouveau Retour Client' : 'Nouveau Retour Fournisseur'}
            </h1>
            <p className="text-white/60 mt-1">
              Enregistrez un {returnType === 'customer' ? 'retour client' : 'retour fournisseur'} avec tous les détails nécessaires
            </p>
          </div>
        </div>
      </div>
      
      {/* Form Container */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 p-6">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Numéro de retour */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  N° de Retour
                </label>
                <input
                  type="text"
                  value={returnData.returnNumber}
                  readOnly
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Date */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Date du retour
                </label>
                <input
                  type="date"
                  value={returnData.date}
                  onChange={(e) => setReturnData({ ...returnData, date: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>
              
              {/* Client/Fournisseur */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  {returnType === 'customer' ? 'Client' : 'Fournisseur'}
                </label>
                <select
                  value={returnData.entityId}
                  onChange={(e) => {
                    const selectedEntity = entities.find(entity => entity.id === e.target.value);
                    if (selectedEntity) {
                      setReturnData({
                        ...returnData, 
                        entityId: e.target.value,
                        entityName: selectedEntity.name,
                        entityEmail: selectedEntity.email || '',
                        entityPhone: selectedEntity.phone || '',
                        entityCompany: selectedEntity.company || '',
                        entityAddress: selectedEntity.address || '',
                        entityWilaya: selectedEntity.wilaya || ''
                      });
                      
                      // Charger l'historique des transactions pour cette entité
                      loadEntityHistory(e.target.value);
                    } else {
                      setReturnData({
                        ...returnData, 
                        entityId: e.target.value,
                        entityName: '',
                        entityEmail: '',
                        entityPhone: '',
                        entityCompany: '',
                        entityAddress: '',
                        entityWilaya: ''
                      });
                    }
                  }}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="">Sélectionner</option>
                  {entities.map((entity) => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                      {returnType === 'customer' && entity.totalTransactions ? 
                        `(${entity.totalTransactions} transactions)` : 
                        returnType === 'supplier' && entity.company ? 
                        `(${entity.company})` : 
                        ''
                      }
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Raison du retour */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Raison du retour
                </label>
                <select
                  value={returnData.reason}
                  onChange={(e) => setReturnData({ ...returnData, reason: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {reasonOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Right Column */}
            <div className="space-y-6">
              {/* Statut */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Statut
                </label>
                <select
                  value={returnData.status}
                  onChange={(e) => setReturnData({ ...returnData, status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="pending">En attente</option>
                  <option value="completed">Accepté</option>
                  <option value="rejected">Refusé</option>
                </select>
              </div>
              
              {/* Méthode de remboursement */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Méthode de {returnType === 'customer' ? 'remboursement' : 'compensation'}
                </label>
                <select
                  value={returnData.refundMethod}
                  onChange={(e) => setReturnData({ ...returnData, refundMethod: e.target.value })}
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {refundMethodOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Notes */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Notes / Commentaires
                </label>
                <textarea
                  value={returnData.notes}
                  onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Ajoutez des notes concernant ce retour..."
                ></textarea>
              </div>
            </div>
          </div>
          
          {/* Entity Information Section - Shows when client or supplier is selected */}
          {returnData.entityId && (
            <div className="mt-6 bg-white/5 rounded-xl p-4 border border-white/10">
              <h3 className="text-white font-medium mb-2">
                {returnType === 'customer' ? 'Informations client' : 'Informations fournisseur'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-white/60 text-sm">Nom</p>
                  <p className="text-white">{returnData.entityName}</p>
                </div>
                {returnData.entityEmail && (
                  <div>
                    <p className="text-white/60 text-sm">Email</p>
                    <p className="text-white">{returnData.entityEmail}</p>
                  </div>
                )}
                {returnData.entityPhone && (
                  <div>
                    <p className="text-white/60 text-sm">Téléphone</p>
                    <p className="text-white">{returnData.entityPhone}</p>
                  </div>
                )}
                {returnType === 'supplier' && returnData.entityCompany && (
                  <div>
                    <p className="text-white/60 text-sm">Entreprise</p>
                    <p className="text-white">{returnData.entityCompany}</p>
                  </div>
                )}
                {returnType === 'supplier' && returnData.entityAddress && (
                  <div>
                    <p className="text-white/60 text-sm">Adresse</p>
                    <p className="text-white">{returnData.entityAddress}</p>
                  </div>
                )}
                {returnType === 'supplier' && returnData.entityWilaya && (
                  <div>
                    <p className="text-white/60 text-sm">Wilaya</p>
                    <p className="text-white">{returnData.entityWilaya}</p>
                  </div>
                )}
              </div>
              
              {returnData.entityHistory && returnData.entityHistory.length > 0 && (
                <div className="mt-4">
                  <p className="text-white/60 text-sm mb-2">
                    {returnType === 'customer' ? 'Historique récent des ventes' : 'Historique récent des achats'}
                  </p>
                  <div className="max-h-32 overflow-y-auto bg-white/5 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-white/10">
                        <tr>
                          <th className="px-3 py-2 text-left text-white">Date</th>
                          <th className="px-3 py-2 text-left text-white">N° {returnType === 'customer' ? 'Facture' : 'Commande'}</th>
                          <th className="px-3 py-2 text-right text-white">Montant</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {returnData.entityHistory.map((transaction, index) => {
                          // Formater correctement les données selon le type de transaction
                          const displayDate = transaction.date || '-';
                          const displayNumber = returnType === 'customer' 
                            ? (transaction.invoiceNumber || transaction.id)
                            : (transaction.purchaseNumber || transaction.orderNumber || transaction.id);
                          
                          // Extraire le montant avec gestion des différents formats possibles
                          let displayAmount = '0';
                          if (typeof transaction.total === 'string' && transaction.total.trim() !== '') {
                            displayAmount = transaction.total;
                          } else if (typeof transaction.total === 'number') {
                            displayAmount = transaction.total.toString();
                          } else if (typeof transaction.totalAmount === 'string' && transaction.totalAmount.trim() !== '') {
                            displayAmount = transaction.totalAmount;
                          } else if (typeof transaction.totalAmount === 'number') {
                            displayAmount = transaction.totalAmount.toString();
                          } else if (typeof transaction.amount === 'string' && transaction.amount.trim() !== '') {
                            displayAmount = transaction.amount;
                          } else if (typeof transaction.amount === 'number') {
                            displayAmount = transaction.amount.toString();
                          } else if (transaction.items && Array.isArray(transaction.items)) {
                            // Calculer le total à partir des éléments si disponibles
                            const total = transaction.items.reduce((sum, item) => {
                              const itemPrice = parseFloat(item.price || 0);
                              const itemQty = parseInt(item.quantity || 1);
                              return sum + (itemPrice * itemQty);
                            }, 0);
                            displayAmount = total.toString();
                          }
                          
                          // Formater le montant pour l'affichage
                          const formattedAmount = parseFloat(displayAmount || 0).toLocaleString() + ' DA';
                          
                          return (
                            <tr key={index} className="hover:bg-white/5">
                              <td className="px-3 py-2 text-white">{displayDate}</td>
                              <td className="px-3 py-2 text-white/80">{displayNumber}</td>
                              <td className="px-3 py-2 text-right text-white">
                                {formattedAmount}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                </div>
              )}
            </div>
          )}
          
          {/* Divider */}
          <div className="my-8 border-t border-white/10"></div>
          
          {/* Products Section */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-white">Articles retournés</h3>
              <button
                type="button"
                onClick={() => setShowProductSearch(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-colors"
              >
                <PlusIcon className="h-5 w-5" />
                <span>Ajouter un article</span>
              </button>
            </div>
            
            {/* Product Search Modal */}
            {showProductSearch && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
                <div className="bg-gray-900 rounded-2xl border border-white/20 p-6 w-full max-w-2xl">
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-xl font-bold text-white">Sélectionner un article</h4>
                    <button
                      onClick={() => {
                        setShowProductSearch(false);
                        setSearchQuery('');
                      }}
                      className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5 text-white" />
                    </button>
                  </div>
                  
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Rechercher un article par nom ou code..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                    <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                  </div>
                  
                  <div className="max-h-64 overflow-y-auto">
                    {filteredProducts.length === 0 ? (
                      <p className="text-white/60 text-center py-4">Aucun produit trouvé</p>
                    ) : (
                      <table className="w-full">
                        <thead className="bg-white/5 text-white/70">
                          <tr>
                            <th className="px-4 py-2 text-left">Code</th>
                            <th className="px-4 py-2 text-left">Nom</th>
                            <th className="px-4 py-2 text-right">Prix</th>
                            <th className="px-4 py-2 text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-white/10">
                          {filteredProducts.map((product) => (
                            <tr key={product.id} className="hover:bg-white/5">
                              <td className="px-4 py-2 text-white font-mono text-sm">{product.code}</td>
                              <td className="px-4 py-2 text-white">{product.name}</td>
                              <td className="px-4 py-2 text-white text-right">{product.price} DA</td>
                              <td className="px-4 py-2 text-center">
                                <button
                                  type="button"
                                  onClick={() => handleAddProduct(product)}
                                  className="px-3 py-1 bg-blue-600/30 hover:bg-blue-600/50 text-blue-400 rounded-lg transition-colors"
                                >
                                  Ajouter
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* Selected Products List */}
            {selectedProducts.length === 0 ? (
              <div className="bg-white/5 border border-white/10 rounded-xl p-12 text-center">
                <p className="text-white/60">
                  Aucun article ajouté. Cliquez sur "Ajouter un article" pour sélectionner les produits retournés.
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5 text-white/70">
                      <tr>
                        <th className="px-4 py-2 text-left">Code</th>
                        <th className="px-4 py-2 text-left">Article</th>
                        <th className="px-4 py-2 text-right">Prix unitaire</th>
                        <th className="px-4 py-2 text-center">Quantité</th>
                        <th className="px-4 py-2 text-right">Total</th>
                        <th className="px-4 py-2 text-center">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {selectedProducts.map((product) => (
                        <tr key={product.id}>
                          <td className="px-4 py-2 text-white font-mono text-sm">{product.code}</td>
                          <td className="px-4 py-2 text-white">{product.name}</td>
                          <td className="px-4 py-2 text-white text-right">{product.price} DA</td>
                          <td className="px-4 py-2">
                            <input
                              type="number"
                              min="1"
                              value={product.quantity}
                              onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                              className="w-16 mx-auto text-center px-2 py-1 bg-white/5 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                            />
                          </td>
                          <td className="px-4 py-2 text-white text-right font-medium">{product.total} DA</td>
                          <td className="px-4 py-2 text-center">
                            <button
                              type="button"
                              onClick={() => handleRemoveProduct(product.id)}
                              className="p-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Total */}
                <div className="mt-6 flex justify-end">
                  <div className="bg-white/5 border border-white/20 rounded-xl px-6 py-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70">Total</span>
                      <span className="text-xl font-bold text-white ml-12">{calculateTotal()} DA</span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Submit Buttons */}
          <div className="mt-8 flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/returns')}
              className="px-6 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 bg-gradient-to-r from-red-600 to-blue-600 text-white rounded-xl hover:from-red-700 hover:to-blue-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Enregistrement...' : 'Enregistrer le retour'}
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
};

export default ReturnForm; 